/**
 * CaptureFields — the click-through form beneath a capture line (Loop's
 * "+ fields"). Binds to the same ParsedState the grammar parses; every
 * edit flows back through `setField`, and the host re-serializes the line
 * so text and form never disagree.
 */
import { X } from "lucide-react";
import { Button } from "@/ui";
import Typeahead from "../Typeahead";
import { TAG_KIND_LABEL, CMDS, SELF_ID, normalizeDate, type Cmd, type Directory, type ParsedState } from "../../lib/grammar";

export type SetField = <K extends keyof ParsedState>(key: K, value: ParsedState[K]) => void;

const HAS_ASSIGNEE = new Set<Cmd>(["action", "owe", "bringup"]);
const HAS_DUE = new Set<Cmd>(["action", "owe", "bringup", "remind"]);

export default function CaptureFields({ state, directory, setField, onCancel, onSubmit, canSubmit }: {
    state: ParsedState;
    directory: Directory;
    setField: SetField;
    onCancel: () => void;
    onSubmit: () => void;
    canSubmit: boolean;
}) {
    const cmd = state.cmd!;
    const assignable = [...directory.contacts, ...directory.companies];
    const dueIso = state.due ? normalizeDate(state.due) : null;
    return (
        <div className="loop-fields" onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onCancel(); } }}>
            <label className="loop-fields-row">
                <span>Type</span>
                <select value={cmd} onChange={(e) => setField("cmd", e.target.value as Cmd)}>
                    {CMDS.map((c) => <option key={c} value={c}>/{c}</option>)}
                </select>
            </label>
            <label className="loop-fields-row">
                <span>{cmd === "remind" ? "Reminder" : cmd === "decision" ? "Decision" : "Title"}</span>
                <input value={state.content} autoFocus placeholder="What is it?"
                    onChange={(e) => setField("content", e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && canSubmit) { e.preventDefault(); onSubmit(); } }} />
            </label>
            {HAS_ASSIGNEE.has(cmd) && (
                <div className="loop-fields-row">
                    <span>{cmd === "action" ? "Owner" : "Contact"}</span>
                    <Typeahead value={state.assignee ?? ""} placeholder="Me" emptyLabel="Me"
                        options={[{ id: SELF_ID, label: "Me" }, ...assignable.map((e) => ({ id: e.id, label: e.name, sublabel: e.kind === "company" ? "company" : null }))]}
                        onChange={(id) => {
                            const hit = assignable.find((e) => e.id === id);
                            setField("assignee", id || null);
                            setField("assigneeKind", hit ? (hit.kind === "company" ? "company" : "contact") : null);
                        }} />
                </div>
            )}
            {HAS_DUE.has(cmd) && (
                <label className="loop-fields-row">
                    <span>{cmd === "remind" ? "Fires" : "Due"}</span>
                    <input type="date" value={dueIso ?? ""} onChange={(e) => setField("due", e.target.value || null)} />
                    {state.due && !dueIso && <em className="loop-chip loop-chip--error">“{state.due}”?</em>}
                </label>
            )}
            <div className="loop-fields-row">
                <span>Tags</span>
                <div className="loop-fields-chips">
                    {state.links.map((l) => (
                        <span key={l.id} className="loop-chip">
                            #{directory.targets.find((t) => t.id === l.id)?.name ?? l.id}
                            <button type="button" className="loop-chip-x" aria-label="Remove tag"
                                onClick={() => setField("links", state.links.filter((x) => x.id !== l.id))}><X size={11} /></button>
                        </span>
                    ))}
                    <Typeahead value="" placeholder="+ account / lead / event / meeting" allowEmpty={false}
                        options={directory.targets.filter((t) => !state.links.some((l) => l.id === t.id))
                            .map((t) => ({ id: t.id, label: t.name, group: t.kind === "contact" ? "Contacts" : TAG_KIND_LABEL[t.kind] }))}
                        onChange={(id) => {
                            const hit = directory.targets.find((t) => t.id === id);
                            if (hit && hit.kind !== "contact") setField("links", [...state.links, { id: hit.id, kind: hit.kind }]);
                        }} />
                </div>
            </div>
            <label className="loop-fields-row loop-fields-row--top">
                <span>Details</span>
                <textarea rows={2} value={state.description} placeholder="Optional body"
                    onChange={(e) => setField("description", e.target.value)} />
            </label>
            <div className="row loop-fields-actions">
                <span className="spacer" />
                <Button size="sm" variant="ghost" onClick={onCancel}>Close</Button>
                <Button size="sm" variant="primary" disabled={!canSubmit} onClick={onSubmit}>Save</Button>
            </div>
        </div>
    );
}
